import { Link } from "react-router-dom";
import { formatCurrency } from "../utils/cmsApi";
import { useSubscription } from "../context/SubscriptionContext";

export default function CompanyConcentration({ companies, npi, name, year }) {
  const { isPro, isBasic, setShowUpgradeModal, setUpgradeMessage } = useSubscription();
  const isPaidTier = isPro || isBasic;
  if (!companies || companies.length === 0) return null;

  const total = companies.reduce((s, c) => s + c.total, 0);
  if (total === 0) return null;

  // Top 5 + Others
  const top5 = companies.slice(0, 5);
  const othersTotal = companies.slice(5).reduce((s, c) => s + c.total, 0);
  const othersCount = companies.slice(5).length;

  const COLORS = [
    "bg-gold-500/70",
    "bg-blue-500/60",
    "bg-teal-500/60",
    "bg-purple-500/60",
    "bg-pink-500/60",
  ];

  const HOVER_COLORS = [
    "hover:bg-gold-500/90",
    "hover:bg-blue-500/80",
    "hover:bg-teal-500/80",
    "hover:bg-purple-500/80",
    "hover:bg-pink-500/80",
  ];

  function exportConcentrationCsv() {
    const rows = [["Company", "Amount ($)", "% of Total", "Payment Count"]];
    for (const c of companies) {
      rows.push([
        c.companyName,
        c.total.toFixed(2),
        `${((c.total / total) * 100).toFixed(1)}%`,
        c.count,
      ]);
    }
    const csv = rows.map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${(name || "provider").replace(/\s+/g, "_")}_company_concentration_${year}.csv`;
    a.click();
  }

  return (
    <div className="card mb-6">
      <h2 className="text-white/40 text-xs uppercase tracking-widest mb-4">
        Company concentration · {year}
      </h2>

      <div className="space-y-3">
        {top5.map((company, i) => {
          const pct = (company.total / total) * 100;
          return (
            <Link
              key={company.companyName}
              to={`/physician/${npi}/company/${encodeURIComponent(company.companyName)}?name=${encodeURIComponent(name)}`}
              className="block group"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-white/70 text-xs truncate max-w-xs group-hover:text-white transition-colors">
                  {company.companyName.length > 45
                    ? company.companyName.slice(0, 45) + "..."
                    : company.companyName}
                </span>
                <div className="flex items-center gap-3 shrink-0 ml-3">
                  <span className="text-white/40 text-xs font-mono">{pct.toFixed(1)}%</span>
                  <span className="text-gold-400/80 text-xs font-mono w-24 text-right">
                    {formatCurrency(company.total)}
                  </span>
                </div>
              </div>
              <div className="w-full bg-white/5 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${COLORS[i]} ${HOVER_COLORS[i]}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </Link>
          );
        })}

        {othersCount > 0 && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-white/40 text-xs">
                {othersCount} other {othersCount === 1 ? "company" : "companies"}
              </span>
              <div className="flex items-center gap-3 shrink-0 ml-3">
                <span className="text-white/30 text-xs font-mono">
                  {((othersTotal / total) * 100).toFixed(1)}%
                </span>
                <span className="text-white/40 text-xs font-mono w-24 text-right">
                  {formatCurrency(othersTotal)}
                </span>
              </div>
            </div>
            <div className="w-full bg-white/5 rounded-full h-2">
              <div
                className="h-2 rounded-full bg-white/20"
                style={{ width: `${(othersTotal / total) * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>

      <div className="print:hidden flex items-center justify-between mt-3">
        <button
          onClick={() => {
            if (!isPaidTier) {
              setUpgradeMessage("Exporting chart data is a Pro feature. Upgrade to download company concentration data as a CSV.");
              setShowUpgradeModal(true);
              return;
            }
            exportConcentrationCsv();
          }}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all"
        >
          {isPaidTier ? "⬇ Export to CSV" : "🔒 Export to CSV"}
        </button>
        <p className="text-white/20 text-xs text-right">
          Based on {formatCurrency(total)} total · click a bar to view details
        </p>
      </div>
    </div>
  );
}